import { Section, CodeBlock, InfoBox } from "@/components"

export function NextTailwindServerClientPage() {
  return (
    <div className="space-y-10">
      <div className="space-y-4">
        <h1 className="text-4xl font-bold">Server & Client Components</h1>
        <p className="text-lg text-muted-foreground max-w-4xl leading-relaxed">
          Understand when to use Server Components vs Client Components in Next.js 14. 
          Make the right choice for optimal performance and user experience.
        </p>
      </div>

      <Section title="Component Types" description="Server vs Client">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-card border rounded-lg p-6 space-y-4">
            <h3 className="text-xl font-semibold">Server Components</h3>
            <p className="text-sm text-muted-foreground">
              Default in App Router. Rendered on the server, never sent to client.
            </p>
            <ul className="space-y-2 text-sm">
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Fetch data directly</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Access backend resources</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Keep secrets safe</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Reduce bundle size</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-card border rounded-lg p-6 space-y-4">
            <h3 className="text-xl font-semibold">Client Components</h3>
            <p className="text-sm text-muted-foreground">
              Opt-in with "use client". Rendered on client, enable interactivity.
            </p>
            <ul className="space-y-2 text-sm">
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Use React hooks</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Handle events</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Use browser APIs</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary">✓</span>
                <span>Add interactivity</span>
              </li>
            </ul>
          </div>
        </div>
      </Section>

      <Section title="Examples with Tailwind" description="Common patterns">
        <div className="space-y-8">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Server Component (Data Fetching)</h3>
            <CodeBlock
              code={`// app/products/page.tsx
// Server Component (no "use client")

async function getProducts() {
  const res = await fetch('https://api.example.com/products', {
    cache: 'no-store'
  })
  return res.json()
}

export default async function ProductsPage() {
  const products = await getProducts()

  return (
    <div className="container mx-auto px-4 py-12">
      <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-8">
        Products
      </h1>
      <div className="grid md:grid-cols-3 gap-6">
        {products.map(product => (
          <div
            key={product.id}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6"
          >
            <h2 className="text-xl font-semibold mb-2">{product.name}</h2>
            <p className="text-gray-600 dark:text-gray-400">\${product.price}</p>
          </div>
        ))}
      </div>
    </div>
  )
}`}
              language="typescript"
            />
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Client Component (Interactivity)</h3>
            <CodeBlock
              code={`// components/AddToCartButton.tsx
"use client"

import { useState } from "react"

interface Props {
  productId: string
  productName: string
}

export function AddToCartButton({ productId, productName }: Props) {
  const [isAdding, setIsAdding] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)

  const addToCart = async () => {
    setIsAdding(true)
    try {
      await fetch("/api/cart", {
        method: "POST",
        body: JSON.stringify({ productId })
      })
      setShowSuccess(true)
      setTimeout(() => setShowSuccess(false), 2000)
    } finally {
      setIsAdding(false)
    }
  }

  return (
    <div>
      <button
        onClick={addToCart}
        disabled={isAdding}
        className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isAdding ? "Adding..." : "Add to Cart"}
      </button>
      {showSuccess && (
        <p className="mt-2 text-sm text-green-600 dark:text-green-400">
          {productName} added to cart!
        </p>
      )}
    </div>
  )
}`}
              language="typescript"
            />
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Composing Server & Client</h3>
            <CodeBlock
              code={`// app/products/page.tsx (Server Component)
import { AddToCartButton } from "@/components/AddToCartButton"

async function getProducts() {
  const res = await fetch('https://api.example.com/products')
  return res.json()
}

export default async function ProductsPage() {
  const products = await getProducts()

  return (
    <div className="grid md:grid-cols-3 gap-6">
      {products.map(product => (
        <div
          key={product.id}
          className="bg-white dark:bg-gray-800 rounded-lg border p-6 space-y-4"
        >
          <h2 className="text-xl font-semibold">{product.name}</h2>
          <p className="text-2xl font-bold text-blue-600">\${product.price}</p>
          <p className="text-gray-600 dark:text-gray-400">{product.description}</p>
          
          {/* Client Component for interactivity */}
          <AddToCartButton 
            productId={product.id}
            productName={product.name}
          />
        </div>
      ))}
    </div>
  )
}`}
              language="typescript"
            />
          </div>
        </div>
      </Section>

      <Section title="Data Fetching Patterns" description="Server Components">
        <div className="space-y-6">
          <CodeBlock
            code={`// Parallel fetching (recommended)
async function Page() {
  const userPromise = getUser()
  const postsPromise = getPosts()
  
  const [user, posts] = await Promise.all([userPromise, postsPromise])
  
  return (
    <div className="space-y-8">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg">
        <h2 className="text-2xl font-bold">{user.name}</h2>
      </div>
      <div className="grid gap-4">
        {posts.map(post => (
          <div key={post.id} className="border rounded-lg p-4">
            {post.title}
          </div>
        ))}
      </div>
    </div>
  )
}

// Streaming with Suspense
import { Suspense } from "react"

async function Page() {
  return (
    <div className="space-y-8">
      <h1 className="text-4xl font-bold">Dashboard</h1>
      
      <Suspense fallback={<UserSkeleton />}>
        <UserData />
      </Suspense>
      
      <Suspense fallback={<PostsSkeleton />}>
        <PostsList />
      </Suspense>
    </div>
  )
}

function UserSkeleton() {
  return (
    <div className="bg-gray-200 dark:bg-gray-700 animate-pulse rounded-lg h-32" />
  )
}`}
            language="typescript"
          />
        </div>
      </Section>

      <div className="bg-primary/5 border border-primary/20 rounded-xl p-8 space-y-4">
        <h2 className="text-2xl font-bold">Best Practices</h2>
        <ul className="space-y-3 text-sm">
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Start with Server Components, only add "use client" when needed</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Move "use client" as deep as possible in the component tree</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Server Components can pass data to Client Components as props</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Use Suspense boundaries for better loading states</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Style both Server and Client Components with Tailwind</span>
          </li>
        </ul>
      </div>
    </div>
  )
}

